import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Shield, FileCheck, Lock, Scan, Sparkles, CheckCircle2, Loader } from 'lucide-react';
import { motion } from 'framer-motion';

const BundleProcessingPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { fileCount = 0, files = [] } = location.state || {};
  const [currentStep, setCurrentStep] = useState(0);
  const [processedDocs, setProcessedDocs] = useState([]);
  const [currentDocIndex, setCurrentDocIndex] = useState(0);

  const steps = [
    { icon: Scan, text: 'Extracting text', color: 'text-verify' },
    { icon: Lock, text: 'Removing sensitive information', color: 'text-warning' },
    { icon: Sparkles, text: 'Analyzing document', color: 'text-trust' },
    { icon: FileCheck, text: 'Complete', color: 'text-verify' },
  ];

  useEffect(() => {
    if (fileCount === 0) {
      navigate('/select-country');
      return;
    }

    const processDocuments = async () => {
      for (let docIdx = 0; docIdx < fileCount; docIdx++) {
        setCurrentDocIndex(docIdx);
        
        // Process each step for this document
        for (let stepIdx = 0; stepIdx < steps.length; stepIdx++) {
          setCurrentStep(stepIdx);
          await new Promise(resolve => setTimeout(resolve, 800));
        }
        
        // Mark document as processed
        setProcessedDocs(prev => [...prev, docIdx]);
      }
      
      // Navigate to bundle results after all processing
      setTimeout(() => {
        navigate('/bundle-results', { 
          state: { 
            fileCount,
            files: files.map((f, i) => ({
              name: f.name || `Document ${i + 1}`,
              type: f.type || 'application/pdf',
              size: f.size || 0
            }))
          } 
        });
      }, 500);
    };

    processDocuments();
  }, [fileCount, navigate]);

  const progress = ((processedDocs.length + (currentStep / steps.length)) / fileCount) * 100;

  return (
    <div className="min-h-screen bg-background flex flex-col items-center justify-center px-6">
      {/* Logo */}
      <div className="mb-12">
        <div className="flex items-center gap-2">
          <Shield className="w-8 h-8 text-trust" />
          <span className="font-outfit font-bold text-2xl text-slate-900">Visa-Ready</span>
        </div>
      </div>

      {/* Processing Animation */}
      <div className="w-full max-w-3xl">
        <div className="bg-white rounded-lg border border-slate-200 p-8 md:p-12 shadow-lg" data-testid="processing-container">
          {/* Overall Progress */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-outfit font-bold text-2xl text-slate-900">Processing Bundle</h2>
              <span className="font-mono text-sm text-slate-600">
                {processedDocs.length} / {fileCount} complete
              </span>
            </div>
            <div className="w-full bg-slate-200 rounded-full h-3 overflow-hidden">
              <motion.div
                className="h-full bg-gradient-to-r from-trust to-verify rounded-full"
                initial={{ width: 0 }}
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.3 }}
              />
            </div>
            <p className="text-sm text-slate-500 mt-2 text-center">
              {Math.round(progress)}% complete
            </p>
          </div>

          {/* Current Document Processing */}
          <div className="mb-8">
            <div className="bg-trust/5 rounded-lg p-6 border border-trust/20">
              <div className="flex items-center gap-3 mb-4">
                {currentDocIndex < fileCount ? (
                  <>
                    <Loader className="w-6 h-6 text-trust animate-spin" />
                    <h3 className="font-public font-semibold text-lg text-slate-900">
                      Processing: {files[currentDocIndex]?.name || `Document ${currentDocIndex + 1}`}
                    </h3>
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="w-6 h-6 text-verify" />
                    <h3 className="font-public font-semibold text-lg text-slate-900">All documents processed!</h3>
                  </>
                )}
              </div>
              
              {/* Current Step */}
              {currentDocIndex < fileCount && (
                <div className="flex items-center gap-2 text-sm text-slate-600">
                  {React.createElement(steps[currentStep].icon, { className: `w-4 h-4 ${steps[currentStep].color}` })}
                  <span>{steps[currentStep].text}...</span>
                </div>
              )}
            </div>
          </div>

          {/* Processed Documents List */}
          {processedDocs.length > 0 && (
            <div className="space-y-2">
              <h4 className="font-public font-semibold text-sm text-slate-700 mb-3">Completed:</h4>
              <div className="max-h-48 overflow-y-auto space-y-2">
                {processedDocs.map((docIdx) => (
                  <motion.div
                    key={docIdx}
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    className="flex items-center gap-2 p-3 bg-verify/5 rounded-md border border-verify/20"
                  >
                    <CheckCircle2 className="w-4 h-4 text-verify" />
                    <span className="font-public text-sm text-slate-700 truncate">
                      {files[docIdx]?.name || `Document ${docIdx + 1}`}
                    </span>
                  </motion.div>
                ))}
              </div>
            </div>
          )}

          {/* Privacy Message */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1 }}
            className="mt-8 pt-8 border-t border-slate-200"
          >
            <div className="flex items-center justify-center gap-2 text-verify">
              <Lock className="w-4 h-4" />
              <span className="font-public text-sm font-medium">All documents are being processed securely</span>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default BundleProcessingPage;
